import { CMS_BASE_URL } from './constants'
import { buildStrapiRequestHeaders, buildStrapiRequestUrl } from './strapiRequest'
import {
  extractStrapiMediaUrl,
  readString,
  toStrapiEntity,
  toStrapiEntityCollection,
} from './utils/strapiPayload'

import type {
  AboutLeadershipMemberCmsContent,
  AboutPageCmsContent,
  AboutStoryMilestoneCmsContent,
} from './interfaces'

interface FetchAboutContentOptions {
  signal?: AbortSignal
  previewToken?: string
}

const ABOUT_PAGE_ENDPOINT = `/api/about-page?${
  [
    'populate[ourStoryMilestones]=true',
    'populate[leadershipTeamMembers][populate][photo][fields][0]=url',
    'populate[leadershipTeamMembers][populate][resumePoints][fields][0]=text',
    'populate[contactButton]=true',
  ].join('&')
}`

const readMediaUrl = (value: unknown): string | null => extractStrapiMediaUrl(value, CMS_BASE_URL)

const mapAboutPageContent = (payload: unknown): AboutPageCmsContent | null => {
  const payloadRoot = toStrapiEntity(payload)
  const contentRoot = toStrapiEntity(payloadRoot?.data || payloadRoot)

  if (!contentRoot) {
    return null
  }

  const milestones = toStrapiEntityCollection(contentRoot.ourStoryMilestones)
    .flatMap((milestone): AboutStoryMilestoneCmsContent[] => {
      const label = readString(milestone.label)
      const description = readString(milestone.description)

      if (label === null || description === null) {
        return []
      }

      return [{ label, description }]
    })

  const leadershipMembers = toStrapiEntityCollection(contentRoot.leadershipTeamMembers)
    .flatMap((member): AboutLeadershipMemberCmsContent[] => {
      const photoUrl = readMediaUrl(member.photo)
      const name = readString(member.name)
      const role = readString(member.role)
      const bio = readString(member.bio)
      const resumePoints = toStrapiEntityCollection(member.resumePoints)
        .map((point) => readString(point.text))
        .filter((point): point is string => point !== null)

      if (
        photoUrl === null ||
        name === null ||
        role === null ||
        bio === null ||
        resumePoints.length === 0
      ) {
        return []
      }

      return [{ photoUrl, name, role, bio, resumePoints }]
    })

  const contactButton = toStrapiEntity(contentRoot.contactButton)

  const heroTitleText = readString(contentRoot.heroTitleText)
  const heroDescription = readString(contentRoot.heroDescription)
  const visionTitle = readString(contentRoot.visionTitle)
  const visionBody = readString(contentRoot.visionBody)
  const ourStoryTitle = readString(contentRoot.ourStoryTitle)
  const leadershipTeamTitle = readString(contentRoot.leadershipTeamTitle)
  const leadershipTeamDescription = readString(contentRoot.leadershipTeamDescription)
  const contactText = readString(contentRoot.contactText)
  const contactButtonText = readString(contactButton?.label)
  const contactButtonUrl = readString(contactButton?.url)

  if (
    heroTitleText === null ||
    heroDescription === null ||
    visionTitle === null ||
    visionBody === null ||
    ourStoryTitle === null ||
    milestones.length === 0 ||
    leadershipTeamTitle === null ||
    leadershipTeamDescription === null ||
    leadershipMembers.length === 0 ||
    contactText === null ||
    contactButtonText === null ||
    contactButtonUrl === null
  ) {
    return null
  }

  return {
    hero: {
      titleText: heroTitleText,
      description: heroDescription,
    },
    vision: {
      title: visionTitle,
      body: visionBody,
    },
    ourStory: {
      title: ourStoryTitle,
      milestones,
    },
    leadership: {
      title: leadershipTeamTitle,
      description: leadershipTeamDescription,
      members: leadershipMembers,
    },
    contact: {
      text: contactText,
      buttonText: contactButtonText,
      buttonUrl: contactButtonUrl,
    },
  }
}

export const fetchAboutContentFromStrapi = async (
  options: FetchAboutContentOptions = {},
): Promise<AboutPageCmsContent | null> => {
  const response = await fetch(buildStrapiRequestUrl(ABOUT_PAGE_ENDPOINT, options.previewToken), {
    method: 'GET',
    signal: options.signal,
    headers: buildStrapiRequestHeaders(options.previewToken),
  })

  if (!response.ok) {
    throw new Error(`Unable to fetch about CMS content (${response.status})`)
  }

  const payload = await response.json() as unknown

  return mapAboutPageContent(payload)
}
